import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { routes } from '../routes';
import {
  listInvitationsForForm, listResponsesForForm, tallyInvitations,
} from '../services/reviewInvitationService';
import {
  computeReviewerBreakdown, type OpticsBreakdown,
} from '../services/opticsScoreEngine';
import {
  REVIEWER_ROLE_LABELS, REVIEWER_ATTRIBUTION,
  type ReviewInvitation, type ReviewResponse, type DirectoryEmployee,
} from '../types/magicLink';
import {
  EMPLOYEE_LEVEL_LABELS, OPTICS_LENS_LABELS, type EmployeeLevel,
} from '../types/optics2026';

type State = 'loading' | 'ready' | 'error';

interface FormDoc {
  id: string;
  cycleId: string;
  revieweeId: string;
  revieweeName: string;
  status: string;
  framework?: string;
}

const STATUS_TONE: Record<string, string> = {
  sent:         'bg-stone-100 text-stone-600',
  opened:       'bg-blue-50 text-blue-700',
  in_progress:  'bg-amber-50 text-amber-700',
  submitted:    'bg-emerald-50 text-emerald-700',
  expired:      'bg-red-50 text-red-700',
};

const MonitorReviewForm: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const [state, setState] = useState<State>('loading');
  const [form, setForm] = useState<FormDoc | null>(null);
  const [reviewee, setReviewee] = useState<Partial<DirectoryEmployee> | null>(null);
  const [invitations, setInvitations] = useState<ReviewInvitation[]>([]);
  const [responses, setResponses] = useState<ReviewResponse[]>([]);
  const [error, setError] = useState('');

  const load = async () => {
    if (!formId) { setState('error'); setError('No formId'); return; }
    try {
      setState('loading');
      const [formSnap, invs, resps] = await Promise.all([
        getDoc(doc(db, 'reviewForms', formId)),
        listInvitationsForForm(formId),
        listResponsesForForm(formId),
      ]);
      if (!formSnap.exists()) {
        setState('error'); setError(`Review form ${formId} not found.`); return;
      }
      const f = { id: formSnap.id, ...formSnap.data() } as FormDoc;
      setForm(f);
      setInvitations(invs);
      setResponses(resps);

      // Lookup reviewee user doc for level
      const userSnap = await getDoc(doc(db, 'users', f.revieweeId));
      if (userSnap.exists()) setReviewee(userSnap.data() as Partial<DirectoryEmployee>);

      setState('ready');
    } catch (e) {
      console.error('[Monitor] failed', e);
      setError(String((e as Error).message ?? e));
      setState('error');
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [formId]);

  const stats = useMemo(() => tallyInvitations(invitations), [invitations]);

  const level: EmployeeLevel = (reviewee?.level as EmployeeLevel) ?? 'general';
  const breakdown: OpticsBreakdown | null = useMemo(
    () => responses.length ? computeReviewerBreakdown(responses, level) : null,
    [responses, level],
  );

  if (state === 'loading') {
    return <div className="p-8 text-stone-500">Loading review monitor…</div>;
  }
  if (state === 'error' || !form) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-800">
          {error}
        </div>
        <Link to="/dispatch" className="text-sm text-blue-600 hover:underline mt-3 inline-block">← Back to dispatcher</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      {/* Header */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-stone-500">Review monitor</div>
        <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">{form.revieweeName}</h1>
        <div className="text-xs text-stone-500 mt-1">
          Form ID: <code className="bg-stone-100 px-1.5 py-0.5 rounded">{form.id}</code>
          {' · '}Cycle: {form.cycleId}
          {' · '}Workflow status: <b>{form.status}</b>
          {reviewee?.level && (<> {' · '}Level: <b>{EMPLOYEE_LEVEL_LABELS[reviewee.level as EmployeeLevel]}</b></>)}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Reviewers"  value={stats.total} sub="invited" />
        <Card label="Submitted"  value={stats.submitted} sub={`${Math.round(stats.submissionRate * 100)}% complete`} tone="ok" />
        <Card label="In progress / opened" value={stats.opened + stats.inProgress} sub="started but unsubmitted" tone={stats.opened + stats.inProgress > 0 ? 'warn' : 'neutral'} />
        <Card label="Pending" value={stats.sent} sub="not yet opened" tone={stats.sent > 0 ? 'warn' : 'neutral'} />
      </div>

      {/* Reviewer status table */}
      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-stone-900 mb-3">Reviewer status</h2>
        {invitations.length === 0 ? (
          <div className="text-xs text-stone-500">No invitations dispatched yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-stone-500 uppercase tracking-wider border-b border-stone-200">
                <th className="py-2">Reviewer</th>
                <th className="py-2">Role</th>
                <th className="py-2">Attribution</th>
                <th className="py-2">Status</th>
                <th className="py-2">Magic link</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map(inv => {
                const attribution = REVIEWER_ATTRIBUTION[inv.reviewerRole];
                // Pin to the canonical domain — see ENGINEERING.md §3e.
                const url = routes.publicReviewInviteUrl(inv.token);
                return (
                  <tr key={inv.id} className="border-b border-stone-100 last:border-0">
                    <td className="py-2.5">
                      <div className="font-medium text-stone-900">{inv.reviewerName ?? '—'}</div>
                      <div className="text-[11px] text-stone-500">{inv.reviewerEmail}</div>
                    </td>
                    <td className="py-2.5">{REVIEWER_ROLE_LABELS[inv.reviewerRole]}</td>
                    <td className="py-2.5 text-xs">
                      <span className={attribution === 'attributed' ? 'text-stone-700' : 'text-amber-700'}>
                        {attribution === 'attributed' ? 'Attributed' : 'Anonymous'}
                      </span>
                    </td>
                    <td className="py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_TONE[inv.status] ?? 'bg-stone-100 text-stone-600'}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-2.5">
                      <button
                        onClick={() => navigator.clipboard.writeText(url)}
                        className="text-[11px] text-blue-600 hover:underline"
                        title="Copy magic link"
                      >
                        Copy
                      </button>
                      {' · '}
                      <a href={`/r/${inv.token}`} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline">
                        Open
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Aggregated OPTICS breakdown */}
      {breakdown ? (
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-sm font-semibold text-stone-900">Aggregated OPTICS breakdown</h2>
            <div className="text-[11px] text-stone-500">
              From {responses.length} response{responses.length === 1 ? '' : 's'} · weighted for{' '}
              <b>{EMPLOYEE_LEVEL_LABELS[breakdown.level]}</b>
              {' '}({Math.round(breakdown.weights.ind * 100)}/{Math.round(breakdown.weights.team * 100)}/{Math.round(breakdown.weights.co * 100)} ind/team/co)
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
            <div className="text-[11px] uppercase tracking-wider text-emerald-700">Overall weighted score</div>
            <div className="text-3xl font-semibold text-emerald-900">{breakdown.overallScore.toFixed(2)}<span className="text-sm text-emerald-700"> / 5.0</span></div>
          </div>

          <div className="space-y-3">
            {breakdown.values.map(v => (
              <div key={v.code} className="border-b border-stone-100 pb-3 last:border-0 last:pb-0">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-bold text-stone-500 w-4">{v.code}</span>
                    <span className="text-sm font-medium text-stone-900">{v.title}</span>
                  </div>
                  <span className="text-base font-semibold tabular-nums">{v.weightedScore.toFixed(2)}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 ml-6">
                  {v.areas.map(a => (
                    <div key={a.id} className="bg-stone-50 rounded p-2">
                      <div className="text-[10px] uppercase tracking-wider text-stone-500">{a.id} · {a.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm font-semibold tabular-nums text-stone-900">{a.weightedScore.toFixed(2)}</span>
                        <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                          <div
                            className={a.weightedScore >= 4 ? 'h-full bg-emerald-500' : a.weightedScore >= 3 ? 'h-full bg-amber-500' : 'h-full bg-red-500'}
                            style={{ width: `${(a.weightedScore / 5) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-1 mt-1 text-[10px] text-stone-500">
                        <span title={OPTICS_LENS_LABELS.ind}>I {a.indScore.toFixed(1)}</span>
                        <span title={OPTICS_LENS_LABELS.team}>T {a.teamScore.toFixed(1)}</span>
                        <span title={OPTICS_LENS_LABELS.co}>C {a.coScore.toFixed(1)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-5 text-sm text-stone-500">
          No responses submitted yet — aggregated breakdown will appear once at least one reviewer submits.
        </div>
      )}

      {/* Footer actions */}
      <div className="flex justify-between items-center pt-2">
        <Link to="/dispatch" className="text-sm text-blue-600 hover:underline">← Back to dispatcher</Link>
        <button onClick={load} className="text-sm bg-stone-900 text-white px-3 py-1.5 rounded-md hover:bg-stone-800">
          Refresh
        </button>
      </div>
    </div>
  );
};

const Card: React.FC<{ label: string; value: number | string; sub: string; tone?: 'ok' | 'warn' | 'neutral' }> = ({ label, value, sub, tone = 'neutral' }) => {
  const accent = tone === 'ok' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-stone-900';
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${accent}`}>{value}</div>
      <div className="text-[11px] text-stone-500">{sub}</div>
    </div>
  );
};

export default MonitorReviewForm;
